import {FC, useCallback, useEffect, useState} from 'react'
import {
  api,
  fileUrl,
  PartnerApplication,
  PartnerApplicationDetail,
  PartnerDocument,
  PartnerServicesView,
  PartnerStatus,
  ServiceCategory,
} from '../../services/api'
import {toast} from '../../utils/toast'

const STATUS_STYLE: Record<PartnerStatus, {bg: string; color: string; label: string}> = {
  draft: {bg: '#f1f5f9', color: '#64748b', label: 'Draft'},
  pending: {bg: '#fef3c7', color: '#b45309', label: 'In review'},
  approved: {bg: '#dcfce7', color: '#16a34a', label: 'Approved'},
  rejected: {bg: '#fef2f2', color: '#dc2626', label: 'Rejected'},
}

const DOC_LABEL: Record<string, string> = {
  drivers_license: "Driver's licence",
  insurance: 'Insurance',
  vehicle_registration: 'Vehicle registration',
  business_registration: 'Business registration',
  tax_id: 'Tax ID',
}

// Which papers block approval. Mirrors REQUIRED_DOCUMENTS in
// business-document.entity.ts, so the screen can say why approval would be
// refused before the button is pressed. The rest are collected but not blocking.
const REQUIRED_DOCS = ['drivers_license', 'insurance']

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

const card = {background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)'}
const input = {width: '100%', padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, color: '#0f172a', background: '#f8fafc', outline: 'none', boxSizing: 'border-box' as const}

const Badge: FC<{status: PartnerStatus}> = ({status}) => {
  const s = STATUS_STYLE[status] ?? STATUS_STYLE.draft
  return (
    <span style={{padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, background: s.bg, color: s.color}}>
      {s.label}
    </span>
  )
}

// One label/value line in the detail panel. Renders an em dash rather than an
// empty gap, so a field the partner skipped is visibly skipped.
const Row: FC<{label: string; children?: React.ReactNode}> = ({label, children}) => (
  <div style={{display: 'flex', gap: 12, padding: '7px 0', borderBottom: '1px solid #f8fafc'}}>
    <span style={{fontSize: 13, color: '#94a3b8', width: 150, flexShrink: 0}}>{label}</span>
    <span style={{fontSize: 13, color: '#0f172a', fontWeight: 500, wordBreak: 'break-word'}}>
      {children === null || children === undefined || children === '' ? '—' : children}
    </span>
  </div>
)

const Section: FC<{title: string; children: React.ReactNode; right?: React.ReactNode}> = ({title, children, right}) => (
  <div style={{...card, padding: '16px 20px', marginBottom: 16}}>
    <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8}}>
      <h3 style={{fontSize: 14, fontWeight: 700, color: '#0f172a', margin: 0}}>{title}</h3>
      {right}
    </div>
    {children}
  </div>
)

const PartnersPage: FC = () => {
  const [rows, setRows] = useState<PartnerApplication[]>([])
  const [categories, setCategories] = useState<ServiceCategory[]>([])
  const [filter, setFilter] = useState<PartnerStatus | ''>('')
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [detail, setDetail] = useState<PartnerApplicationDetail | null>(null)
  // Whichever of the two the partner's serving mode calls for — a shop's priced
  // catalogue rows, or a home-service partner's own listing.
  const [servicesView, setServicesView] = useState<PartnerServicesView | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [busy, setBusy] = useState(false)

  // One dialog for both verdicts. A rejection collects the reason the partner
  // will read; an approval just asks whether you mean it, since it puts them in
  // front of customers.
  const [ask, setAsk] = useState<{
    title: string
    body: string
    confirmLabel: string
    tone: 'approve' | 'reject'
    requireReason: boolean
    onConfirm: (reason: string) => Promise<void> | void
  } | null>(null)
  const [reasonText, setReasonText] = useState('')

  useEffect(() => {
    api.getServiceCategories().then(setCategories).catch(() => {})
  }, [])

  const reload = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      setRows(await api.getPartnerApplications(filter || undefined))
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load partner applications')
    } finally {
      setLoading(false)
    }
  }, [filter])
  useEffect(() => { reload() }, [reload])

  // `silent` refreshes the data in place after a review action. Without it the
  // spinner replaces the whole drawer, unmounting the scrolled content, and the
  // reader is thrown back to the top of a list they were part-way through.
  const loadDetail = useCallback(async (id: string, silent = false) => {
    if (!silent) setDetailLoading(true)
    try {
      const [d, s] = await Promise.all([
        api.getPartnerApplication(id),
        api.getPartnerServices(id).catch(() => null),
      ])
      setDetail(d)
      setServicesView(s)
    } catch (e: any) {
      toast(e?.message ?? 'Failed to load the application', 'error')
      if (!silent) setSelectedId(null)
    } finally {
      if (!silent) setDetailLoading(false)
    }
  }, [])

  useEffect(() => {
    if (selectedId) loadDetail(selectedId)
    else { setDetail(null); setServicesView(null) }
  }, [selectedId, loadDetail])

  const visible = rows.filter(r =>
    !search.trim() ||
    `${r.businessName} ${r.ownerName} ${r.email ?? ''}`.toLowerCase().includes(search.trim().toLowerCase()),
  )

  const categoryName = (id: string) => categories.find(c => c.id === id)?.label ?? '—'

  const rejectDoc = async (doc: PartnerDocument, reason: string) => {
    setBusy(true)
    try {
      await api.reviewPartnerDocument(doc.id, {status: 'rejected', rejectionReason: reason})
      toast('Document rejected')
      if (selectedId) await loadDetail(selectedId, true)
      reload()
    } catch (e: any) {
      toast(e?.message ?? 'Failed', 'error')
    } finally {
      setBusy(false)
    }
  }

  const approveDoc = async (doc: PartnerDocument) => {
    setBusy(true)
    try {
      await api.reviewPartnerDocument(doc.id, {status: 'approved'})
      toast('Document approved')
      if (selectedId) await loadDetail(selectedId, true)
      reload()
    } catch (e: any) {
      toast(e?.message ?? 'Failed', 'error')
    } finally {
      setBusy(false)
    }
  }

  const reviewDoc = (doc: PartnerDocument, approve: boolean) => {
    const label = DOC_LABEL[doc.type] ?? doc.type
    setReasonText('')
    if (!approve) {
      setAsk({
        title: `Reject ${label}`,
        body: 'The partner is shown this reason, so say what is wrong with the document and what would fix it.',
        confirmLabel: 'Reject document',
        tone: 'reject',
        requireReason: true,
        onConfirm: reason => rejectDoc(doc, reason),
      })
      return
    }
    setAsk({
      title: `Approve ${label}?`,
      body: REQUIRED_DOCS.includes(doc.type)
        ? 'This is one of the documents that must be approved before the partner can be let in. Check the file itself first.'
        : 'Confirm you have checked the file and it is what it claims to be.',
      confirmLabel: 'Approve document',
      tone: 'approve',
      requireReason: false,
      onConfirm: () => approveDoc(doc),
    })
  }

  const submitReview = async (approved: boolean, reason?: string) => {
    if (!detail) return
    setBusy(true)
    try {
      await api.reviewPartner(detail.id, {approved, rejectionReason: reason})
      toast(approved ? 'Partner approved — they are now visible to customers' : 'Partner rejected')
      await loadDetail(detail.id, true)
      reload()
    } catch (e: any) {
      // The server refuses approval while a required document is unapproved or
      // out of date; surfacing its message is more useful than a generic error.
      toast(e?.message ?? 'Failed', 'error')
    } finally {
      setBusy(false)
    }
  }

  const reviewPartner = (approve: boolean) => {
    if (!detail) return
    setReasonText('')
    if (!approve) {
      setAsk({
        title: `Reject ${detail.businessName}`,
        body: 'The partner is shown this reason and can correct what is wrong and submit again.',
        confirmLabel: 'Reject partner',
        tone: 'reject',
        requireReason: true,
        onConfirm: reason => submitReview(false, reason),
      })
      return
    }
    setAsk({
      title: `Approve ${detail.businessName}?`,
      body:
        'They become visible to customers straight away, at the prices they set, ' +
        'and are awarded the verified tick. You can still suspend them afterwards.',
      confirmLabel: 'Approve partner',
      tone: 'approve',
      requireReason: false,
      onConfirm: () => submitReview(true),
    })
  }

  // Exactly what the server checks in reviewBusiness, so the button is enabled
  // only when pressing it will actually work.
  const blockers = (d: PartnerApplicationDetail) => {
    const out: string[] = []
    for (const type of REQUIRED_DOCS) {
      const doc = d.documents.find(x => x.type === type)
      if (!doc) out.push(`${DOC_LABEL[type]} not uploaded`)
      else if (doc.status !== 'approved') out.push(`${DOC_LABEL[type]} not approved`)
      else if (doc.expiresAt && new Date(doc.expiresAt) < new Date()) out.push(`${DOC_LABEL[type]} has expired`)
    }
    return out
  }

  // Worth saying, but not grounds to refuse: the server approves a partner with
  // no pin, they simply never surface in a distance search.
  const warnings = (d: PartnerApplicationDetail) =>
    d.latitude == null || d.longitude == null
      ? ['No map pin — they will not appear in search even once approved']
      : []

  return (
    <div>
      <div style={{marginBottom: 24}}>
        <h1 style={{fontSize: 24, fontWeight: 700, color: '#0f172a', margin: '0 0 4px'}}>Partner Applications</h1>
        <p style={{fontSize: 14, color: '#64748b', margin: 0}}>
          Everything a partner submitted during onboarding — check the documents, then let them in or keep them out
        </p>
      </div>

      {/* Toolbar */}
      <div style={{...card, padding: '16px 20px', marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap'}}>
        <input placeholder='Search by business, owner or email...' value={search}
          onChange={e => setSearch(e.target.value)} style={{...input, flex: 1, minWidth: 220, background: '#fff'}} />
        <select value={filter} onChange={e => setFilter(e.target.value as PartnerStatus | '')}
          style={{...input, width: 'auto', minWidth: 170, flex: '0 1 auto'}}>
          <option value=''>All statuses</option>
          <option value='pending'>In review</option>
          <option value='approved'>Approved</option>
          <option value='rejected'>Rejected</option>
          <option value='draft'>Draft</option>
        </select>
        <button onClick={reload} disabled={loading}
          style={{padding: '10px 16px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, fontWeight: 500, color: '#374151', background: '#f8fafc', cursor: 'pointer'}}>
          Refresh
        </button>
      </div>

      {error && (
        <div style={{padding: '12px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#dc2626', fontSize: 14, marginBottom: 16}}>
          {error}
        </div>
      )}

      {/* List */}
      <div style={{...card, overflow: 'hidden'}}>
        {loading ? (
          <div style={{padding: '60px 0', textAlign: 'center'}}>
            <div style={{width: 32, height: 32, border: '3px solid #e2e8f0', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto'}} />
          </div>
        ) : (
          <div style={{overflowX: 'auto'}}>
            <table style={{width: '100%', borderCollapse: 'collapse', fontSize: 14}}>
              <thead>
                <tr style={{background: '#f8fafc', borderBottom: '1px solid #e2e8f0'}}>
                  {['PARTNER', 'CONTACT', 'DOCUMENTS', 'STATUS', ''].map(h => (
                    <th key={h} style={{padding: '11px 20px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap'}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visible.map((r, i) => (
                  <tr key={r.id} style={{borderBottom: i < visible.length - 1 ? '1px solid #f1f5f9' : 'none'}}>
                    <td style={{padding: '14px 20px'}}>
                      <div style={{display: 'flex', alignItems: 'center', gap: 12}}>
                        {r.imageUrl ? (
                          <img src={fileUrl(r.imageUrl)} alt={r.businessName}
                            style={{width: 40, height: 40, borderRadius: 8, objectFit: 'cover', border: '1px solid #e2e8f0'}} />
                        ) : (
                          <div style={{width: 40, height: 40, borderRadius: 8, background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontWeight: 700}}>
                            {r.businessName?.[0]?.toUpperCase() ?? '?'}
                          </div>
                        )}
                        <div>
                          <div style={{fontWeight: 700, color: '#0f172a'}}>{r.businessName}</div>
                          <div style={{fontSize: 12, color: '#94a3b8'}}>{r.ownerName}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{padding: '14px 20px', color: '#64748b'}}>
                      <div>{r.email ?? '—'}</div>
                      <div style={{fontSize: 12}}>{r.phone ?? ''}</div>
                    </td>
                    <td style={{padding: '14px 20px'}}>
                      <span style={{fontSize: 13, color: r.documentsValid ? '#16a34a' : '#b45309'}}>
                        {r.documents.filter(d => d.status === 'approved').length}/{r.documents.length} approved
                      </span>
                    </td>
                    <td style={{padding: '14px 20px'}}><Badge status={r.approvalStatus} /></td>
                    <td style={{padding: '14px 20px', textAlign: 'right'}}>
                      <button onClick={() => setSelectedId(r.id)}
                        style={{padding: '6px 14px', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: 13, fontWeight: 500, color: '#374151', background: '#fff', cursor: 'pointer'}}>
                        Review
                      </button>
                    </td>
                  </tr>
                ))}
                {visible.length === 0 && (
                  <tr><td colSpan={5} style={{padding: '48px 20px', textAlign: 'center', color: '#94a3b8'}}>No applications</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail drawer */}
      {selectedId && (
        <div onClick={() => setSelectedId(null)}
          style={{position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 200, display: 'flex', justifyContent: 'flex-end'}}>
          <div onClick={e => e.stopPropagation()}
            style={{background: '#f8fafc', width: 620, maxWidth: '100%', height: '100%', overflowY: 'auto', boxShadow: '-12px 0 40px rgba(0,0,0,0.12)'}}>

            <div style={{padding: '18px 24px', background: '#fff', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 1}}>
              <h2 style={{fontSize: 18, fontWeight: 700, margin: 0, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                {detail?.businessName ?? 'Application'}
              </h2>
              <button onClick={() => setSelectedId(null)}
                style={{background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 20}}>✕</button>
            </div>

            {detailLoading || !detail ? (
              <div style={{padding: '80px 0', textAlign: 'center'}}>
                <div style={{width: 32, height: 32, border: '3px solid #e2e8f0', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto'}} />
              </div>
            ) : (
              <div style={{padding: 20}}>

                {/* Who this is, at a glance, before any of the detail. */}
                <div style={{...card, padding: '16px 20px', marginBottom: 16}}>
                  <div style={{display: 'flex', alignItems: 'center', gap: 12}}>
                    {detail.imageUrl ? (
                      <img src={fileUrl(detail.imageUrl)} alt='' style={{width: 56, height: 56, borderRadius: 10, objectFit: 'cover', border: '1px solid #e2e8f0'}} />
                    ) : null}
                    <div style={{flex: 1}}>
                      <Badge status={detail.approvalStatus} />
                      {detail.isVerified && (
                        <span style={{marginLeft: 8, fontSize: 12, color: '#2563eb', fontWeight: 600}}>✓ Verified</span>
                      )}
                      <div style={{fontSize: 12, color: '#94a3b8', marginTop: 4}}>
                        Submitted {detail.submittedAt ? new Date(detail.submittedAt).toLocaleString() : '—'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Step 1 */}
                <Section title='Company profile'>
                  <Row label='Company name'>{detail.businessName}</Row>
                  <Row label='Owner'>{detail.ownerName}</Row>
                  <Row label='Email'>{detail.email}</Row>
                  <Row label='Phone'>{detail.phone}</Row>
                  <Row label='Licence number'>{detail.licenseNumber}</Row>
                  <Row label='Street'>{detail.street}</Row>
                  <Row label='Apartment'>{detail.apartment}</Row>
                  <Row label='City'>{detail.city}</Row>
                  <Row label='State'>{detail.state}</Row>
                  <Row label='ZIP'>{detail.postalCode}</Row>
                  <Row label='Country'>{detail.country}</Row>
                  <Row label='Map pin'>
                    {detail.latitude != null && detail.longitude != null ? (
                      <a href={`https://maps.google.com/?q=${detail.latitude},${detail.longitude}`} target='_blank' rel='noreferrer' style={{color: '#3b82f6'}}>
                        {detail.latitude.toFixed(5)}, {detail.longitude.toFixed(5)}
                      </a>
                    ) : null}
                  </Row>
                  <Row label='About'>{detail.description}</Row>
                </Section>

                {/* Step 2 */}
                <Section title='Serving modes and hours'>
                  <Row label='Serves'>
                    {[detail.servesAtCustomer && 'At the customer', detail.servesAtShop && 'At their shop']
                      .filter(Boolean).join(' · ') || null}
                  </Row>
                  <Row label='Working days'>
                    {detail.workingDays?.length ? detail.workingDays.map(d => DAYS[d]).join(', ') : null}
                  </Row>
                  <Row label='Hours'>
                    {detail.openTime && detail.closeTime ? `${detail.openTime} – ${detail.closeTime}` : null}
                  </Row>
                </Section>

                {/* Step 3 */}
                <Section
                  title='Services and prices'
                  right={
                    <span style={{fontSize: 12, color: '#94a3b8'}}>
                      {servicesView?.mode === 'home'
                        ? 'Home Service'
                        : `${servicesView?.offerings.length ?? 0} total`}
                    </span>
                  }>
                  {/* A partner who travels to the customer describes one
                      service in their own words and picks what they do out of
                      the admin's own lists; a shop prices catalogue rows. Two
                      different answers to the same question. */}
                  {servicesView?.mode === 'home' ? (
                    !servicesView.listing ? (
                      <div style={{fontSize: 13, color: '#94a3b8', padding: '8px 0'}}>Nothing set yet.</div>
                    ) : (
                      <>
                        <Row label='Service'>{servicesView.listing.title}</Row>
                        <Row label='Category'>{servicesView.listing.category?.label}</Row>
                        <Row label='Does'>
                          {servicesView.listing.group
                            ? servicesView.listing.group.label
                            : null}
                        </Row>
                        <Row label='Offers'>
                          {servicesView.listing.services.length ? (
                            <span style={{display: 'flex', flexWrap: 'wrap', gap: 6}}>
                              {servicesView.listing.services.map(v => (
                                <span key={v.id} style={{fontSize: 12, fontWeight: 600, color: '#0369a1', background: '#e0f2fe', padding: '2px 9px', borderRadius: 999}}>
                                  {v.name}
                                </span>
                              ))}
                            </span>
                          ) : null}
                        </Row>
                        <Row label='Experience'>
                          {servicesView.listing.experienceYears == null
                            ? null
                            : `${servicesView.listing.experienceYears} year${servicesView.listing.experienceYears === 1 ? '' : 's'}`}
                        </Row>
                        <Row label='About'>{servicesView.listing.description}</Row>
                        <Row label='Keywords'>{servicesView.listing.keywords}</Row>

                        {servicesView.listing.packages.length > 0 && (
                          <div style={{marginTop: 12, display: 'flex', gap: 10, flexWrap: 'wrap'}}>
                            {servicesView.listing.packages.map(p => (
                              <div key={p.tier} style={{flex: '1 1 150px', minWidth: 150, border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 12px', background: '#f8fafc'}}>
                                <div style={{fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em'}}>{p.tier}</div>
                                <div style={{fontSize: 13, fontWeight: 700, color: '#0f172a', marginTop: 3}}>{p.name}</div>
                                {p.description && <div style={{fontSize: 12, color: '#64748b', marginTop: 2}}>{p.description}</div>}
                                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: 8}}>
                                  <span style={{fontSize: 13, fontWeight: 700, color: '#16a34a'}}>{p.cost}</span>
                                  {p.time && <span style={{fontSize: 12, color: '#94a3b8'}}>{p.time}</span>}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )
                  ) : !servicesView?.offerings.length ? (
                    <div style={{fontSize: 13, color: '#94a3b8', padding: '8px 0'}}>Nothing set yet.</div>
                  ) : (
                    servicesView.offerings.map(s => (
                      <div key={s.id} style={{display: 'flex', alignItems: 'center', gap: 12, padding: '9px 0', borderBottom: '1px solid #f8fafc'}}>
                        <div style={{flex: 1}}>
                          <div style={{fontSize: 13, fontWeight: 600, color: '#0f172a'}}>
                            {s.name}
                            {s.isCustom && (
                              <span style={{marginLeft: 6, fontSize: 11, fontWeight: 600, color: '#7c3aed', background: '#f5f3ff', padding: '1px 7px', borderRadius: 10}}>own</span>
                            )}
                            {!s.isActive && (
                              <span style={{marginLeft: 6, fontSize: 11, color: '#94a3b8'}}>(off)</span>
                            )}
                          </div>
                          <div style={{fontSize: 12, color: '#94a3b8'}}>
                            {categoryName(s.categoryId)}
                            {s.subtitle ? ` · ${s.subtitle}` : ''}
                            {s.features?.length ? ` · ${s.features.length} included` : ''}
                          </div>
                        </div>
                        <div style={{textAlign: 'right'}}>
                          <div style={{fontSize: 13, fontWeight: 700, color: '#16a34a'}}>${Number(s.price).toFixed(2)}</div>
                          {s.durationMinutes ? <div style={{fontSize: 12, color: '#94a3b8'}}>{s.durationMinutes} min</div> : null}
                        </div>
                      </div>
                    ))
                  )}
                </Section>

                {/* Step 4 */}
                <Section title='Documents'>
                  {detail.documents.length === 0 ? (
                    <div style={{fontSize: 13, color: '#94a3b8', padding: '8px 0'}}>Nothing uploaded yet.</div>
                  ) : (
                    detail.documents.map(d => {
                      const expired = d.expiresAt ? new Date(d.expiresAt) < new Date() : false
                      return (
                        <div key={d.id} style={{padding: '10px 0', borderBottom: '1px solid #f8fafc'}}>
                          <div style={{display: 'flex', alignItems: 'center', gap: 10}}>
                            <div style={{flex: 1}}>
                              <div style={{fontSize: 13, fontWeight: 600, color: '#0f172a'}}>
                                {DOC_LABEL[d.type] ?? d.type}
                                {REQUIRED_DOCS.includes(d.type) && (
                                  <span style={{marginLeft: 6, fontSize: 11, color: '#ef4444'}}>required</span>
                                )}
                              </div>
                              <div style={{fontSize: 12, color: expired ? '#dc2626' : '#94a3b8'}}>
                                {d.expiresAt ? `Expires ${d.expiresAt}${expired ? ' — expired' : ''}` : 'No expiry'}
                              </div>
                            </div>
                            <a href={fileUrl(d.url)} target='_blank' rel='noreferrer'
                              style={{fontSize: 13, color: '#3b82f6', textDecoration: 'none'}}>View</a>
                            <span style={{padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                              background: d.status === 'approved' ? '#dcfce7' : d.status === 'rejected' ? '#fef2f2' : '#fef3c7',
                              color: d.status === 'approved' ? '#16a34a' : d.status === 'rejected' ? '#dc2626' : '#b45309'}}>
                              {d.status}
                            </span>
                          </div>
                          {d.rejectionReason && (
                            <div style={{fontSize: 12, color: '#dc2626', marginTop: 4}}>Reason: {d.rejectionReason}</div>
                          )}
                          <div style={{display: 'flex', gap: 8, marginTop: 8}}>
                            <button onClick={() => reviewDoc(d, true)} disabled={busy || d.status === 'approved'}
                              style={{padding: '5px 12px', border: '1px solid #bbf7d0', borderRadius: 7, fontSize: 12, fontWeight: 600, color: '#16a34a', background: '#fff', cursor: d.status === 'approved' ? 'default' : 'pointer', opacity: d.status === 'approved' ? 0.5 : 1}}>
                              Approve
                            </button>
                            <button onClick={() => reviewDoc(d, false)} disabled={busy}
                              style={{padding: '5px 12px', border: '1px solid #fecaca', borderRadius: 7, fontSize: 12, fontWeight: 600, color: '#ef4444', background: '#fff', cursor: 'pointer'}}>
                              Reject
                            </button>
                          </div>
                        </div>
                      )
                    })
                  )}
                </Section>

                {/* The verdict, read last — after the profile, the services and
                    the documents it is a judgement on. */}
                <div style={{...card, padding: '16px 20px', marginBottom: 16}}>
                  <h3 style={{fontSize: 14, fontWeight: 700, color: '#0f172a', margin: '0 0 10px'}}>Decision</h3>
                  {detail.rejectionReason && (
                    <div style={{padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#dc2626', fontSize: 13, marginBottom: 12}}>
                      Rejected: {detail.rejectionReason}
                    </div>
                  )}

                  <div style={{display: 'flex', gap: 10}}>
                    {(() => {
                      const done = detail.approvalStatus === 'approved'
                      // Held shut until the documents the server insists on are
                      // approved and in date, so the button never offers an
                      // action that would come straight back as an error.
                      const held = blockers(detail).length > 0
                      return (
                        <button
                          onClick={() => reviewPartner(true)}
                          disabled={busy || done || held}
                          title={held ? `Approve the required documents first: ${blockers(detail).join('; ')}` : undefined}
                          style={{
                            padding: '9px 18px', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600,
                            color: '#fff',
                            background: done || held ? '#cbd5e1' : '#16a34a',
                            cursor: done || held ? 'not-allowed' : 'pointer',
                          }}>
                          {done ? 'Approved' : 'Approve partner'}
                        </button>
                      )
                    })()}
                    <button onClick={() => reviewPartner(false)} disabled={busy}
                      style={{padding: '9px 18px', border: '1px solid #fecaca', borderRadius: 8, fontSize: 14, fontWeight: 600, color: '#ef4444', background: '#fff', cursor: 'pointer'}}>
                      Reject
                    </button>
                  </div>

                  {blockers(detail).length > 0 && detail.approvalStatus !== 'approved' && (
                    <div style={{padding: '10px 14px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, color: '#b45309', fontSize: 13, marginTop: 12}}>
                      <strong>Approve these first:</strong>
                      <ul style={{margin: '6px 0 0', paddingLeft: 18}}>
                        {blockers(detail).map(b => <li key={b}>{b}</li>)}
                      </ul>
                    </div>
                  )}

                  {warnings(detail).map(w => (
                    <div key={w} style={{padding: '10px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, color: '#64748b', fontSize: 13, marginTop: 12}}>
                      {w}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Verdict dialog. Sits above the drawer, so the application stays in
          view behind it while the decision is made. */}
      {ask && (
        <div onClick={() => setAsk(null)}
          style={{position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20}}>
          <div onClick={e => e.stopPropagation()}
            style={{...card, width: 480, maxWidth: '100%', overflow: 'hidden'}}>
            <div style={{padding: '20px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
              <h3 style={{fontSize: 17, fontWeight: 700, color: '#0f172a', margin: 0}}>{ask.title}</h3>
              <button onClick={() => setAsk(null)}
                style={{background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 20}}>✕</button>
            </div>

            <div style={{padding: 24}}>
              <p style={{fontSize: 13, color: '#64748b', margin: ask.requireReason ? '0 0 12px' : 0, lineHeight: 1.6}}>
                {ask.body}
              </p>
              {ask.requireReason && (
                <>
                  <textarea
                    autoFocus
                    rows={4}
                    value={reasonText}
                    onChange={e => setReasonText(e.target.value)}
                    placeholder='e.g. The document is blurred and the expiry date cannot be read.'
                    style={{...input, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5}}
                  />
                  {!reasonText.trim() && (
                    <div style={{fontSize: 11, color: '#94a3b8', marginTop: 6}}>A reason is required.</div>
                  )}
                </>
              )}
            </div>

            <div style={{padding: '16px 24px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', gap: 10}}>
              <button onClick={() => setAsk(null)} disabled={busy}
                style={{padding: '9px 20px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', fontSize: 14, cursor: 'pointer', color: '#374151'}}>
                Cancel
              </button>
              {(() => {
                const ready = !ask.requireReason || Boolean(reasonText.trim())
                return (
                  <button
                    onClick={async () => {
                      const reason = reasonText.trim()
                      if (ask.requireReason && !reason) return
                      const run = ask.onConfirm
                      setAsk(null)
                      await run(reason)
                    }}
                    disabled={busy || !ready}
                    style={{
                      padding: '9px 20px', borderRadius: 8, border: 'none', fontSize: 14, fontWeight: 600, color: '#fff',
                      background: !ready ? '#cbd5e1' : ask.tone === 'approve' ? '#16a34a' : '#ef4444',
                      cursor: ready ? 'pointer' : 'not-allowed',
                    }}>
                    {ask.confirmLabel}
                  </button>
                )
              })()}
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  )
}

const PartnersPageWrapper: FC = () => <PartnersPage />

export {PartnersPageWrapper}
